import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Button, Dropdown, Header, Icon, Modal } from '@adc/ui-core'
import classnames from 'classnames/bind'
import SaveSearchModal from '../saveSearchModal/index'
import ActionIcon from '../actionIcon'
import { SAVED_SEARCH_TYPES } from '../constants/savedSearches'
import styles from './styles.scss'
const cx = classnames.bind(styles)

class SavedSearches extends PureComponent {
  static propTypes = {
    isDirty: PropTypes.bool,
    isUserGlobalSearchManager: PropTypes.bool.isRequired,
    searches: PropTypes.array.isRequired,
    selectedSearch: PropTypes.number,
    onCreate: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
  }

  state = {
    isSaveSearchModalOpen: false,
    isCreateMode: false,
    isDeleteConfirmationModalOpen: false,
    deleteCandidateId: null,
  }

  /**
   * Sorts the list with globals first (then by name) and then adds headers and options to the list.
   *
   * Note: we are stretching the semantic dropdown a bit here as because they haven't fully
   * accounted for a search select with multiple headers like this.  Headers are more of a "menu" kind
   * of thing although they did account for a single header on a searchable, select list.  All that being said:
   *
   * a) we need to use the shorthand approach (passing options instead of building out menu directly)
   *    which in turn is necessary to make search work correctly.
   * b) given (a) we are treating headers as options (and disabling and fixing the styling)
   *
   * That latter bit is the kludge, but in the end it works fine.  The headers are basically disabled options
   * which don't really do anything other than look pretty.
   *
   * @param {Array} searches
   */
  prepOptions = searches => {
    const sortedSearches = _.sortBy(searches, [
      'saved_search_type',
      'saved_search_name',
    ])
    const options = []
    for (let i = 0; i < sortedSearches.length; i++) {
      const item = sortedSearches[i]
      if (
        item.saved_search_type === SAVED_SEARCH_TYPES.GLOBAL &&
        !options.length
      ) {
        options.push(
          this.prepHeaderOption({
            key: 'global_header',
            text: 'global',
            iconName: 'globe',
          })
        )
      } else if (
        item.saved_search_type === SAVED_SEARCH_TYPES.PERSONAL &&
        (i === 0 ||
          sortedSearches[i - 1].saved_search_type === SAVED_SEARCH_TYPES.GLOBAL)
      ) {
        options.push(
          this.prepHeaderOption({
            key: 'personal_header',
            text: 'personal',
            iconName: 'user',
          })
        )
      }
      options.push(this.prepOption(item))
    }

    return options
  }

  canUserDelete = item =>
    item.saved_search_type === SAVED_SEARCH_TYPES.PERSONAL ||
    (item.saved_search_type === SAVED_SEARCH_TYPES.GLOBAL &&
      this.props.isUserGlobalSearchManager)

  onDelete = item => {
    if (item.saved_search_type === SAVED_SEARCH_TYPES.GLOBAL) {
      // GLOBAL - we need confirmation
      this.setState({
        isDeleteConfirmationModalOpen: true,
        deleteCandidate: item,
      })
    } else {
      // PERSONAL - don't bother with confirmation
      this.props.onDelete(item.saved_search_id)
    }
  }

  onConfirmDelete = () => {
    this.props.onDelete(this.state.deleteCandidate.saved_search_id)
    this.closeDeleteConfirmationModal()
  }

  closeDeleteConfirmationModal = () => {
    this.setState({
      deleteCandidate: null,
      isDeleteConfirmationModalOpen: false,
    })
  }

  renderDeleteConfirmationModal = () => (
    <Modal
      closeOnDimmerClick={false}
      elmId='delete_confirmation_modal'
      size='tiny'
      open={this.state.isDeleteConfirmationModalOpen}
      onClose={this.closeDeleteConfirmationModal}
      closeIcon={false}
      mobileTitleSkin='light'
    >
      <Header data-elm-id='delete_confirmation_modal_header'>
        <Icon name='warning sign' size='big' color='caution' />
        <Header.Content>Delete Global Saved Search</Header.Content>
      </Header>
      <Modal.Content>
        <p>
          You are about to delete a <strong>global</strong> saved search. This
          action cannot be undone and will preclude others from using it as
          well.
        </p>
        <p>Are you sure?</p>
      </Modal.Content>
      <Modal.Actions>
        <Button
          size='small'
          elmId='button_cancel'
          onClick={this.closeDeleteConfirmationModal}
        >
          No
        </Button>
        <Button
          size='small'
          role='primary'
          elmId='button_accept'
          onClick={this.onConfirmDelete}
        >
          Yes - Delete It
        </Button>
      </Modal.Actions>
    </Modal>
  )

  renderListItem = item => (
    <div className={cx('item')}>
      <div className={cx('item-label')}>{item.saved_search_name}</div>
      {this.canUserDelete(item) && (
        <Icon
          name='trash'
          data-elm-id={`delete_search_${item.saved_search_id}`}
          onClick={() => {
            this.onDelete(item)
          }}
          className={cx('item-delete')}
        />
      )}
    </div>
  )

  prepOption = item => ({
    key: item.saved_search_id,
    text: item.saved_search_name,
    value: item.saved_search_id,
    content: this.renderListItem(item),
  })

  prepHeaderOption = ({ key, text, iconName }) => ({
    disabled: true,
    key,
    text,
    value: key,
    className: cx('item-header'),
    content: (
      <Dropdown.Header
        icon={iconName}
        content={text}
        className={cx('header')}
      />
    ),
    style: { opacity: 1 },
  })

  getSelectedSearchName = withDirtyIndicator => {
    const id = this.props.selectedSearch
    if (_.isNil(id)) return ''
    return `${this.props.isDirty && withDirtyIndicator ? '*' : ''}${_.get(
      _.find(this.props.searches, { saved_search_id: id }),
      'saved_search_name',
      ''
    )}`
  }

  getSelectedSearchType = () => {
    const id = this.props.selectedSearch
    if (_.isNil(id)) return ''
    return _.find(this.props.searches, { saved_search_id: id })
      .saved_search_type
  }

  onSave = () => {
    this.setState({
      isSaveSearchModalOpen: true,
      isCreateMode: !this.props.selectedSearch,
    })
  }

  onCreate = (name, type) => {
    this.onCloseSaveNewSearch()
    this.props.onCreate(name, type)
  }

  onUpdate = () => {
    this.onCloseSaveNewSearch()
    this.props.onUpdate()
  }

  onCloseSaveNewSearch = () => {
    this.setState({ isSaveSearchModalOpen: false })
  }

  onChange = (e, data) => {
    // only change if not clicking delete - in which case onDelete will take over
    if (!e.target.className.includes('trash')) this.props.onSelect(data.value)
  }

  render() {
    const {
      selectedSearch = null,
      searches,
      isUserGlobalSearchManager,
    } = this.props
    const { isSaveSearchModalOpen, isCreateMode } = this.state
    return (
      <div className={cx('container')}>
        {this.renderDeleteConfirmationModal()}
        <Dropdown
          compact
          text={this.getSelectedSearchName(true)}
          data-elm-id='saved-search-dropdown'
          placeholder='Select Saved Search'
          selection
          search
          options={this.prepOptions(searches)}
          openOnFocus={false}
          value={selectedSearch}
          selectOnBlur={false}
          onChange={this.onChange}
          className={cx('search-dropdown')}
        />
        <ActionIcon
          data-elm-id='saved-search-save-button'
          name='save'
          onClick={this.onSave}
        />
        <SaveSearchModal
          defaultName={isCreateMode ? '' : this.getSelectedSearchName(false)}
          bOpen={isSaveSearchModalOpen}
          onCreate={this.onCreate}
          onUpdate={this.onUpdate}
          onClose={this.onCloseSaveNewSearch}
          isCreateMode={isCreateMode}
          isUserGlobalSearchManager={isUserGlobalSearchManager}
          currentSavedSearchType={this.getSelectedSearchType()}
          searches={searches}
        />
      </div>
    )
  }
}

export default SavedSearches
